import React, { useEffect, useState, useCallback } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { ja } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import "./DocumentRegister.css";

registerLocale("ja", ja);

// 現在の日付取得 YYYY/MM/DD
const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}/${month}/${day}`;
};

const PatternKeisokuForm = ({
  onFormDataChange,
  documentCategories = [],
  documentTypes = [],
  currentUserName = "",
  optionsLoading = false,
  resetToken = 0,
}) => {
  // 文字幅をカウントする関数-------------------------------------------------------Start
  const getCharacterWidth = (text) => {
    if (!text) return 0;
    let width = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const code = char.charCodeAt(0);
      // Check if character is full-width (zenkaku)
      if (
        (code >= 0x3040 && code <= 0x309f) || // Hiragana
        (code >= 0x30a0 && code <= 0x30ff) || // Katakana
        (code >= 0x4e00 && code <= 0x9fff) || // CJK Ideographs
        (code >= 0xff00 && code <= 0xffef) || // Full-width ASCII
        (code >= 0x3000 && code <= 0x303f) // Full-width symbols
      ) {
        width += 2; // Full-width characters count as 2
      } else {
        width += 1; // Half-width characters count as 1
      }
    }
    return width;
  };

  // Convert YYYY/MM/DD string to Date object
  const stringToDate = (dateString) => {
    if (!dateString) return null;
    // Parse YYYY/MM/DD format by splitting
    const parts = dateString.split("/");
    if (parts.length === 3) {
      const [year, month, day] = parts.map(Number);
      const date = new Date(year, month - 1, day);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    return null;
  };

  // Convert Date object to YYYY/MM/DD string
  const dateToString = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}/${month}/${day}`;
  };

  const getInitialFormData = useCallback(
    () => ({
      document_category: "", // 図面種類
      date: getCurrentDate(), // 日付
      category_name: "", // 図面名称
      assignee: currentUserName || "", // 担当者
      product_name: "", // 製品名
      document_type: "", // 図面タイプ
      product_model: "", // 製品形式
      original_doc_number: "", // 元図番
      purpose: "", // 用途
      remarks: "", // 備考
      customer: "", // 得意先
    }),
    [currentUserName]
  );

  const [formData, setFormData] = useState(() => getInitialFormData());

  useEffect(() => {
    const resetData = getInitialFormData();
    setFormData(resetData);
    if (onFormDataChange) {
      onFormDataChange(resetData);
    }
  }, [resetToken, currentUserName, onFormDataChange, getInitialFormData]);

  // IME変換中の状態を追跡するためのステート
  const [isComposing, setIsComposing] = useState(false);

  // IME変換の開始処理
  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  // IME変換の終了処理
  const handleCompositionEnd = (e) => {
    setIsComposing(false);
    // Force validation after composition
    validateAndUpdate(e);
  };

  // 検証および更新を行う関数
  const validateAndUpdate = (e) => {
    const { name, value } = e.target;

    // 各フィールドの最大文字数定義
    const fieldMaxWidths = {
      category_name: 30, // 図面名称 - 全角15文字
      assignee: 20, // 担当者 - 全角10文字
      product_name: 20, // 製品名 - 全角10文字
      product_model: 20, // 製品形式 - 全角10文字
      original_doc_number: 20, // 元図番 - 全角10文字
      purpose: 40, // 用途 - 全角20文字
      remarks: 40, // 備考 - 全角20文字
      customer: 40, // 得意先 - 全角20文字
    };

    const maxWidth = fieldMaxWidths[name];
    if (maxWidth) {
      const currentWidth = getCharacterWidth(value);
      if (currentWidth > maxWidth) {
        // 最大文字数に合わせて切り詰める
        let truncatedValue = "";
        let width = 0;
        for (let i = 0; i < value.length; i++) {
          const char = value[i];
          const code = char.charCodeAt(0);
          const charWidth =
            (code >= 0x3040 && code <= 0x309f) || // Hiragana
            (code >= 0x30a0 && code <= 0x30ff) || // Katakana
            (code >= 0x4e00 && code <= 0x9fff) || // CJK Ideographs
            (code >= 0xff00 && code <= 0xffef) || // Full-width ASCII
            (code >= 0x3000 && code <= 0x303f) // Full-width symbols
              ? 2
              : 1;

          if (width + charWidth > maxWidth) break;
          truncatedValue += char;
          width += charWidth;
        }

        const truncatedFormData = {
          ...formData,
          [name]: truncatedValue,
        };
        setFormData(truncatedFormData);

        if (onFormDataChange) {
          onFormDataChange(truncatedFormData);
        }
        return;
      }
    }

    // 通常の更新
    const newFormData = {
      ...formData,
      [name]: value,
    };
    setFormData(newFormData);

    if (onFormDataChange) {
      onFormDataChange(newFormData);
      console.log("PatternKeisokuForm data:", newFormData);
    }
  };

  // 入力変更処理
  const handleChange = (e) => {
    const { name, value } = e.target;

    // 常に先に値更新
    const newFormData = {
      ...formData,
      [name]: value,
    };
    setFormData(newFormData);

    // IME変換中は検証スキップ
    if (isComposing) {
      return;
    }

    // IME変換以外の変更時に検証を行う
    validateAndUpdate(e);
  };

  // 文字幅をカウントする関数-------------------------------------------------------End

  // Highlight weekends function
  const highlightWeekends = (date) => {
    const day = date.getDay();
    if (day === 0) {
      return "highlight-sunday";
    } else if (day === 6) {
      return "highlight-saturday";
    }
    return "";
  };

  // Date change handler
  const handleDateChange = (date) => {
    const newFormData = {
      ...formData,
      date: dateToString(date), // Format YYYY/MM/DD
    };
    setFormData(newFormData);

    if (onFormDataChange) {
      onFormDataChange(newFormData);
    }
  };

  const drawingTypeOptions = (documentCategories || []).map((item) => ({
    value: String(item.document_category),
    label: item.document_category_text,
  }));

  const drawingType2Options = (documentTypes || []).map((item) => ({
    value: String(item.document_type),
    label: item.document_type_text,
  }));

  // Debug
  console.log("PatternKeisokuForm - documentCategories:", documentCategories);
  console.log("PatternKeisokuForm - documentTypes:", documentTypes);
  console.log("PatternKeisokuForm - drawingTypeOptions:", drawingTypeOptions);
  console.log("PatternKeisokuForm - drawingType2Options:", drawingType2Options);
  console.log("PatternKeisokuForm - formData:", formData);

  return (
    <div className="pattern-form">
      <div className="form-grid">
        {/* １行目: 図面種類 + 日付 */}
        <div className="form-row">
          <label className="form-label">
            図面種類（計測） <span className="required">*</span>
          </label>
          <select
            name="document_category"
            value={formData.document_category}
            onChange={handleChange}
            className="form-select"
            disabled={optionsLoading}
          >
            <option value=""></option>
            {drawingTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <label className="form-label">
            日付 <span className="required">*</span>
          </label>
          <div className="mod-form__text calendar">
            <DatePicker
              selected={stringToDate(formData.date)}
              onChange={handleDateChange}
              dayClassName={highlightWeekends}
              locale="ja"
              dateFormat="yyyy年MM月dd日"
              className="custom-datepicker"
              calendarStartDay={1}
              placeholderText="日付を選択"
              renderCustomHeader={({
                date,
                changeYear,
                changeMonth,
                decreaseMonth,
                increaseMonth,
              }) => {
                const currentYear = new Date().getFullYear();
                const years = Array.from(
                  { length: 30 },
                  (_, i) => currentYear - i
                );

                const months = [
                  "01",
                  "02",
                  "03",
                  "04",
                  "05",
                  "06",
                  "07",
                  "08",
                  "09",
                  "10",
                  "11",
                  "12",
                ];

                return (
                  <div
                    className="custom-header"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 8px",
                    }}
                  >
                    {/* Prev button */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        decreaseMonth();
                      }}
                    >
                      {"<"}
                    </button>

                    {/* Year + Month display + dropdown */}
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <select
                          className="year-select"
                          value={date.getFullYear()}
                          onChange={(e) => changeYear(e.target.value)}
                          style={{ padding: "2px 4px" }}
                        >
                          {years.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                        <span>年</span>
                      </div>

                      <div>
                        <select
                          value={date.getMonth()}
                          onChange={(e) => changeMonth(e.target.value)}
                          style={{ padding: "2px 4px" }}
                          className="month-select"
                        >
                          {months.map((m, i) => (
                            <option key={i} value={i}>
                              {m}
                            </option>
                          ))}
                        </select>
                        <span>月</span>
                      </div>
                    </div>

                    {/* Next button */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        increaseMonth();
                      }}
                    >
                      {">"}
                    </button>
                  </div>
                );
              }}
              onKeyDown={(e) => e.preventDefault()}
            />
          </div>
        </div>

        {/* 2行目: 図面名称 + 担当者 */}
        <div className="form-row">
          <label className="form-label">
            図面名称 <span className="required">*</span>
          </label>
          <input
            type="text"
            name="category_name"
            value={formData.category_name}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={30}
          />
        </div>

        <div className="form-row">
          <label className="form-label">
            担当者 <span className="required">*</span>
          </label>
          <input
            type="text"
            name="assignee"
            value={formData.assignee}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={20}
          />
        </div>

        {/* ３行目: 製品名 + 図面ﾀｲﾌﾟ */}
        <div className="form-row">
          <label className="form-label">製品名</label>
          <input
            type="text"
            name="product_name"
            value={formData.product_name}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={20}
          />
        </div>

        <div className="form-row">
          <label className="form-label">図面タイプ（計測）</label>
          <select
            name="document_type"
            value={formData.document_type}
            onChange={handleChange}
            className="form-select"
            disabled={optionsLoading}
          >
            <option value=""></option>
            {drawingType2Options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* ４行目: 製品形式 + 元図番 */}
        <div className="form-row">
          <label className="form-label">
            製品形式 <span className="required">*</span>
          </label>
          <input
            type="text"
            name="product_model"
            value={formData.product_model}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={20}
          />
        </div>

        <div className="form-row">
          <label className="form-label">元図番</label>
          <input
            type="text"
            name="original_doc_number"
            value={formData.original_doc_number}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={20}
          />
        </div>

        {/* ５行目: 用途 + 備考 */}
        <div className="form-row">
          <label className="form-label">用途</label>
          <input
            type="text"
            name="purpose"
            value={formData.purpose}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={40}
          />
        </div>

        <div className="form-row">
          <label className="form-label">備考</label>
          <input
            type="text"
            name="remarks"
            value={formData.remarks}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={40}
          />
        </div>
        {/* ６行目: 得意先 + プレースホルダー */}
        <div className="form-row">
          <label className="form-label">得意先</label>
          <input
            type="text"
            name="customer"
            value={formData.customer}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={40}
          />
        </div>
      </div>
    </div>
  );
};

export default PatternKeisokuForm;
